import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { DragDropContext } from '@hello-pangea/dnd';
import { Box } from '@mui/material';
import { Banner } from '@/common/banner/Banner';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNotif } from '@/common/notif/NotifContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useEffect, useState } from 'react';
import { EntitySlot } from '../entitySlot/EntitySlot';

/**
 * EntityBoard is a kanban-style board which renders EntitySlots (with EntityCards)
 * which are configured using SlotSpecs.
 * 
 * EntityBoard itself is responsible for rendering the Slots, managing drag and drop
 * and displaying title elements like Title, Buttons, and Banners
 * 
 * @typedef {Object} SlotSpec
 * @prop {string} id A defined id/key for the Slot
 * @prop {string} title Title
 * @prop {string} entity Relevent entity for the Slot
 * @prop {any} rowsReq Used for retrieving rows of the entity
 * @prop {string[]} fields Slots will render fields based on field defs, this list gives the subset to show and their order
 * @prop {(entityObj:any)=>{index:number,tooltip:string}} [priority] (Optional) A function that returns an object with 'index' ( 1-10, 10+ overflow), and optional tooltip message
 * @prop {(fromSlotId:string)=>boolean} [canDropFrom] (Optional) A function that returns whether an EntityCard can be dropped from a different slot
 * @prop {({entityId:number,fromSlotId:string})=>void} [onDrop] (Optional) A function that defines behaviour to commence on dropping an EntityCard
 */
export const EntityBoard = ({ icon, title, buttons, slotSpecs }) => {

  const module = useModuleFromRoute();
  const rsrc = useRsrc();
  const [dragStart, setDragStart] = useState(null);
  const [jobs, setJobs] = useState([]); // For banner
  const [slotEntities, setSlotEntities] = useState({}); // Need to pass entityObj to drag related callbacks

  const handleDragStart = ({ source, draggableId }) => {
    const entityId = parseInt(draggableId.split('|')[1], 10);
    const entityObj = slotEntities[source.droppableId]?.find(slotEntityObj => slotEntityObj.id === entityId);
    setDragStart({ fromSlotId: source.droppableId, entityObj });
  };

  const handleDragEnd = (result) => {
    setDragStart(null);
    const { source, destination, draggableId } = result;
    // nothing to do if dropped outside a list
    if (!destination) return;

    const fromSlot = slotSpecs.find(s => s.id === source.droppableId);
    const toSlot = slotSpecs.find(s => s.id === destination.droppableId);

    const entityId = parseInt(draggableId.split('|')[1], 10);
    const entityObj = slotEntities[fromSlot.id]?.find(slotEntityObj => slotEntityObj.id === entityId);

    // if you’re moving between different slots
    if (fromSlot && toSlot && fromSlot.id !== toSlot.id) {
      // call the destination’s onDrop if it exists
      if (typeof toSlot.onDrop === 'function') {
        toSlot.onDrop({
          fromSlotId: fromSlot.id,
          entityObj,
        });
      }
    }
  };

  const handleEntitiesLoaded = (slotId, entities) => {
    setSlotEntities(m => ({ ...m, [slotId]: entities }));
  };

  let entities = new Set();
  slotSpecs.map(slotSpec => entities.add(slotSpec.entity));
  useEffect(() => {

    const rowsReq = {
      expr: ExprUtils.and(
        ExprUtils.whereParams('module', module, 'entity', [...entities], 'status', 'RUN'),
        ExprUtils.isNot('cat', 'APPR', 'EXPORT')
      )
    }

    rsrc.rows('skdr', 'JOB', rowsReq).then(jobs => {
      setJobs(jobs)
    })
  }, []);


  const { notifs } = useNotif();
  useEffect(() => {
    if (!notifs || notifs.length === 0) return;

    setJobs(prevJobs => {
      let updatedJobs = [...prevJobs];

      // Add notif.event.job if it matches the criteria
      notifs.forEach(notif => {
        const job = notif.event?.job;
        if (job && job.module === module && entities.has(job.entity) && !updatedJobs.some(j => j.id === job.id) && !['APPR', 'EXPORT'].includes(job.cat)) {
          updatedJobs.push(notif.event.job);
        }
      });

      // Remove jobs for which any notification has an eventType starting with "FINISH"
      // and a matching job id.
      updatedJobs = updatedJobs.filter(j => {
        let jobCompleted = false;
        notifs.forEach(notif => {
          const job = notif.event?.job;
          if (job && job.id === j.id && notif.eventType.startsWith('FINISH')) {
            jobCompleted = true;
          }
        });
        return !jobCompleted;
      });
      return updatedJobs;
    });
  }, [notifs]);


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh', // full viewport height
        overflow: 'hidden',
      }}
    >

      {/* Title and Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 1, mt: 1 }} >
        <Title icon={icon} title={title} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} >
          {buttons}
        </Box>
      </Box>


      {/* Banner */}
      <Box>
        {jobs.map(job => <Banner key={job.id} icon={fas.faExclamationTriangle} text={'Running ' + job.name} />)}
      </Box>


      {/* Main Board */}
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box
          sx={{
            flex: 1, // takes up remaining vertical space
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'auto',
            padding: 2,
          }}
        >
          {slotSpecs.map((slotSpec, index) => {
            return <EntitySlot key={index} slotSpec={slotSpec} dragStart={dragStart} onEntitiesLoaded={handleEntitiesLoaded} />;
          })}
        </Box>
      </DragDropContext>
    </Box>
  );
}